import { observable, action, computed, set, entries, toJS } from 'mobx';
import qs from 'qs';
import merge from 'lodash/merge';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import isPlainObject from 'lodash/isPlainObject';
import isEqual from 'lodash/isEqual';
import { isMobile } from 'mobile-device-detect';
import FORM_CONFIG from '~/src/config/form';
import {
  DEMO_GALLERY_CONFIG
} from '~/src/config/galleries';
import trackEvent, {
  debouncedTracker,
  eventNames
} from '~/src/services/analytics';

const cloudFromUrl = qs.parse(window.location.search, { ignoreQueryPrefix: true }).cloudName;
const codeConfigOnly = qs.parse(window.location.search, { ignoreQueryPrefix: true }).code === 'configjson'
  ? true
  : undefined;

export default class EditorStore {
  formConfig = FORM_CONFIG;
  orgFormConfig = false;
  orgWidgetConfig = false;

  isFormStateNeedReset = false;

  @observable isMobile = isMobile;
  @observable orgWidgetConfigInit = false;
  @observable formState = cloudFromUrl
    ? { cloudName: cloudFromUrl }
    : DEMO_GALLERY_CONFIG;

  @observable codeConfigOnly = codeConfigOnly;

  @action
  setFormValue = (propertyName, value, group) => {
    if (group) {
      let prop = this.formState[group] ? this.formState[group] : {};
      prop[propertyName] = value;
      //reset in order the change to take affect;
      set(this.formState, group, null);
      set(this.formState, group, prop);
    }
    else {
      set(this.formState, propertyName, value);
    }
    // Analytics
    if (propertyName != 'mode' && propertyName != 'columns') {
      debouncedTracker({
        action: eventNames[group] && eventNames[group][propertyName]
          || eventNames[propertyName]
          || propertyName,
        label: eventNames.labels[propertyName] && eventNames.labels[propertyName][value]
          || eventNames.labels[propertyName]
          || value
      });
    }
  };

  @action
  setFormConfig = config => {
    const jsConfig = merge({}, toJS(config));
    this.orgFormConfig = jsConfig;
    set(this, 'formState', jsConfig);
    trackEvent({ action: 'navigation', label: 'try it' });
  };

  @action
  initFormState = config => {
    if (config.displayProps) {
      config.layout = config.displayProps.mode + (config.displayProps.columns || '1');
    }
    this.orgWidgetConfig = config;
    this.orgWidgetConfigInit = true;
  };

  @action
  resetForm = () => {
    trackEvent({ action: 'reset demo', label: 'reset gallery widget configuration' });
    if (this.orgFormConfig && this.orgWidgetConfig) {
      this.isFormStateNeedReset = true;
      set(this, 'formState', this.orgFormConfig);
    }
  }

  @action
  setDefaults = () => {
    if (!this.orgFormConfig) {
      const jsConfig = Object.assign({}, toJS(this.formState));
      this.orgFormConfig = jsConfig;
    }
  }

  @action
  setCodeConfigOnly = (codeConfigOnly) => {
    this.codeConfigOnly = codeConfigOnly;
  }

  @computed get getFormState() {
    let props = {};
    entries(this.formState).map(([key, value]) => {
      props[key] = value;
    });

    if (props.zoomProps) {
      if (props.zoomProps.type == 'popup') {
        props.zoomProps = merge({},
          props.zoomProps,
          {
            trigger: 'click',
            steps: Number.isInteger(props.zoomProps.steps)
              ? props.zoomProps.steps
              : 3
          });
      } else {
        props.zoomProps = merge({}, props.zoomProps, { steps: 0 });
      }
    }

    if (props.displayProps) {
      props.layout = props.displayProps.mode + (props.displayProps.columns || '1');
    }

    return props;
  }

  @computed get getFormStateForForm() {
    const props = Object.assign({}, this.getFormState, { container: '#my-gallery' });

    if (this.orgWidgetConfigInit) {

      // Set default button colors
      props.navigationButtonProps = merge(
        props.navigationButtonProps,
        {
          color: get(props, 'navigationButtonProps.color') || get(props, 'themeProps.primary', this.orgWidgetConfig.themeProps.primary),
          iconColor: get(props, 'navigationButtonProps.iconColor') || get(props, 'themeProps.onPrimary', this.orgWidgetConfig.themeProps.onPrimary)
        }
      );

      props.thumbnailProps = merge(
        props.thumbnailProps,
        {
          navigationColor: get(props, 'thumbnailProps.navigationColor') || get(props, 'themeProps.primary', this.orgWidgetConfig.themeProps.primary),
          navigationIconColor: get(props, 'thumbnailProps.navigationIconColor') || get(props, 'themeProps.onPrimary', this.orgWidgetConfig.themeProps.onPrimary)
        }
      );

      return merge({}, this.orgWidgetConfig, props);
    }
    else {
      return props;
    }
  }

  @computed get processWidgetParams() {
    const props = Object.assign({}, this.getFormState, { container: '#my-gallery' });
    if (props.aspectRatio === 'more') {
      if (props.moreAspectRatio) {
        props.aspectRatio = props.moreAspectRatio;
      }
      else {
        delete props.aspectRatio;
      }
    }
    if (props.zoomProps) {
      props.zoomProps = Object.assign({}, props.zoomProps, {});
      if (props.zoomProps.viewerPosition) {
        props.zoomProps.showLens = props.zoomProps.viewerPosition !== 'inline';
      }
    }
    return props;
  }

  @computed get widgetPropsFromFormState() {
    let props = this.processWidgetParams;

    if (this.isFormStateNeedReset) {
      props = Object.assign({}, props, this.orgWidgetConfig);
      if (this.orgFormConfig && Array.isArray(this.orgFormConfig.viewportBreakpoints)) {
        // empty array will not merge over a populated array so we force it
        props.viewportBreakpoints = this.orgFormConfig.viewportBreakpoints;
      }
      this.isFormStateNeedReset = false;
    }

    return props;
  }

  @computed get formCode() {
    let output = '';
    let config = merge({}, this.processWidgetParams); // cloneDeep

    // Manipulate exported code (remove demo-specific fields)
    delete config.layout;
    delete config.moreAspectRatio;
    delete config.logErrors;
    delete config.analytics;
    delete config.zoom;
    delete config.viewportBreakpoints;
    if (config.displayProps) delete config.displayProps.topOffset;

    // Remove default values
    config = removeDefaults(config, cloudinary.galleryWidgetInfo.DEFAULTS);

    if (this.codeConfigOnly) {
      delete config.cloudName;
      delete config.container;
      delete config.mediaAssets;
      output = window.js_beautify(JSON.stringify(config));
    }
    else {
      output = window.js_beautify(`const myWidget = cloudinary.galleryWidget(\n  ${JSON.stringify(config)}\n);\nmyWidget.render();`);
    }

    return output;
  }
}

// Helper function - Remove default values from 'config' object
const removeDefaults = function (config, defaults) {
  return reduce(config, function (result, value, key) {
    if (isPlainObject(value)) {
      result[key] = removeDefaults(value, defaults[key]);
      if (isEmpty(result[key])) delete result[key];
    } else if (value && !isEqual(value, defaults[key])) {
      result[key] = value;
    }
    return result;
  }, {});
};
